import { useState, type CSSProperties } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { LoadingBlock } from '@/components/PageHeader';
import { errorMessage, inputStyle } from '@/lib/format';

import { FORM_DESK_STYLES, previewJournal } from './formPreview';
import { DEFAULT_FORMS } from './journalForm';
import { JournalPaper } from './JournalPaper';
import { defaultChecks } from './patrolChecklist';
import {
  DEFAULT_PATROL_HEADER,
  usePatrolTemplate,
  usePatrolTemplateWrite,
  type PatrolHeader,
  type PatrolTemplateItem,
} from './safeJournalApi';

/**
 * 순회점검일지 양식 편집 (V535 · V540 · DOC-122 §14 · §20) — 종이 머리 글(제목 · 부제 · 결재 칸 · 칸 이름)과
 * 점검 항목(구분 · 점검내용) 회사 공통 한 벌. 왼쪽에서 고치면 오른쪽 종이가 바로 바뀐다
 * (사용자 지시 2026-10-23 「미리보기화면이 우측에 있어야」 · 「엑셀표 자체를 어느정도 편집도」).
 *
 * <p>고치는 사람은 안전보건팀 · 최고관리자다. 버튼은 감추지 않는다 — 그 밖의 사람이 [저장] 을 누르면 서버가 답한다.
 * 점검 항목은 그 뒤에 새로 쓰는 일지부터 쓰인다. 머리 글은 종이의 틀이라 옛 일지의 종이 · PDF 도 따른다.
 */
export function PatrolTemplateEditor({ onClose }: { onClose: () => void }) {
  const template = usePatrolTemplate();
  if (!template.data && !template.isError) {
    return (
      <Modal title="순회점검일지 양식" onClose={onClose} maxWidth="1380px">
        <LoadingBlock />
      </Modal>
    );
  }
  // 서버가 아직 옛 판이면(404 · header 없음) 화면 상수에서 시작한다 — 저장은 서버가 새 판이 된 뒤에 된다.
  const items = template.data?.items ?? defaultChecks().map((one) => ({ category: one.category, item: one.item }));
  return <Form initialHeader={template.data?.header ?? DEFAULT_PATROL_HEADER} initial={items} onClose={onClose} />;
}

function Form({
  initialHeader,
  initial,
  onClose,
}: {
  initialHeader: PatrolHeader;
  initial: PatrolTemplateItem[];
  onClose: () => void;
}) {
  const [header, setHeader] = useState<PatrolHeader>(initialHeader);
  const [rows, setRows] = useState<PatrolTemplateItem[]>(initial);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const write = usePatrolTemplateWrite();

  const touch = () => {
    setDirty(true);
    setSaved(false);
  };
  const change = (next: PatrolTemplateItem[]) => {
    setRows(next);
    touch();
  };
  const head = (next: Partial<PatrolHeader>) => {
    setHeader((prev) => ({ ...prev, ...next }));
    touch();
  };
  const set = (index: number, next: Partial<PatrolTemplateItem>) => change(rows.map((row, at) => (at === index ? { ...row, ...next } : row)));
  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    [next[index], next[to]] = [next[to]!, next[index]!];
    change(next);
  };
  const setSigner = (at: 0 | 1, value: string) => {
    const next = [header.signers[0] ?? '', header.signers[1] ?? ''];
    next[at] = value;
    head({ signers: next[1] ? next : [next[0]!] });
  };
  const setResult = (at: number, value: string) => head({ resultLabels: header.resultLabels.map((one, i) => (i === at ? value : one)) });

  const save = () => {
    setError(null);
    write.mutate(
      { header, items: rows },
      {
        onSuccess: (data) => {
          setRows(data.items);
          if (data.header) setHeader(data.header);
          setDirty(false);
          setSaved(true);
        },
        onError: (cause) => setError(errorMessage(cause)),
      },
    );
  };
  const reset = () => {
    setHeader(DEFAULT_PATROL_HEADER);
    touch();
  };

  const cell: CSSProperties = { ...inputStyle, padding: '4px 6px', fontSize: '13px' };
  const icon: CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' };
  const field = (label: string, value: string, onChange: (value: string) => void) => (
    <label className="sf-field">
      <span>{label}</span>
      <input className="input" style={cell} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );

  // 미리보기 — 점검 항목만 든 빈 일지. 결과 칸은 비어 있다.
  const paper = previewJournal('PATROL', { checks: rows.map((row) => ({ category: row.category, item: row.item, result: null })) });

  return (
    <Modal
      title="순회점검일지 양식"
      onClose={onClose}
      maxWidth="1380px"
      closeDisabled={write.isPending}
      beforeClose={() => !dirty || window.confirm('저장하지 않은 양식이 있습니다. 닫을까요?')}
      actions={
        <div className="flex items-center gap-2">
          <button className="btn-secondary" disabled={write.isPending} onClick={reset}>
            머리 글 기본값으로
          </button>
          <button className="btn-primary" disabled={write.isPending} onClick={save}>
            {write.isPending ? '저장 중...' : '저장'}
          </button>
        </div>
      }
    >
      <style>{FORM_DESK_STYLES}</style>
      <div className="sf-desk">
        <div className="sf-side">
          {error && <Notice tone="warning">{error}</Notice>}
          {saved && (
            <Notice tone="info" closable onClose={() => setSaved(false)}>
              저장했습니다.
            </Notice>
          )}
          <div className="sf-panel">
            <div className="sf-title">머리</div>
            <div className="sf-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {field('제목', header.title, (v) => head({ title: v }))}
              {field('부제', header.subtitle, (v) => head({ subtitle: v }))}
            </div>
            <div className="sf-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {field('결재 칸 1', header.signers[0] ?? '', (v) => setSigner(0, v))}
              {field('결재 칸 2', header.signers[1] ?? '', (v) => setSigner(1, v))}
            </div>
            <div className="sf-row" style={{ gridTemplateColumns: '1fr 2fr' }}>
              {field('구분 칸', header.categoryLabel, (v) => head({ categoryLabel: v }))}
              {field('점검 내용 칸', header.itemLabel, (v) => head({ itemLabel: v }))}
            </div>
            <div className="sf-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {field('결과 1', header.resultLabels[0] ?? '', (v) => setResult(0, v))}
              {field('결과 2', header.resultLabels[1] ?? '', (v) => setResult(1, v))}
              {field('결과 3', header.resultLabels[2] ?? '', (v) => setResult(2, v))}
            </div>
            {field('맨 아래 칸', header.noteLabel, (v) => head({ noteLabel: v }))}
          </div>
          <div className="sf-panel">
            <div className="sf-title">점검 항목</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ width: '26px', padding: '4px 2px' }}>No</th>
                  <th style={{ width: '92px', padding: '4px 2px' }}>{header.categoryLabel || '구분'}</th>
                  <th style={{ padding: '4px 2px' }}>{header.itemLabel || '점검 내용'}</th>
                  <th style={{ width: '62px' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td style={{ padding: '2px', fontSize: '12px', color: 'var(--text-muted)' }}>{index + 1}</td>
                    <td style={{ padding: '2px' }}>
                      <input className="input" style={cell} value={row.category ?? ''} onChange={(e) => set(index, { category: e.target.value || null })} />
                    </td>
                    <td style={{ padding: '2px' }}>
                      <input className="input" style={cell} value={row.item} onChange={(e) => set(index, { item: e.target.value })} />
                    </td>
                    <td style={{ padding: '2px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button type="button" style={icon} aria-label="위로" onClick={() => move(index, -1)}>
                        ↑
                      </button>
                      <button type="button" style={icon} aria-label="아래로" onClick={() => move(index, 1)}>
                        ↓
                      </button>
                      <button type="button" style={icon} aria-label="줄 삭제" onClick={() => change(rows.filter((_, at) => at !== index))}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: '12px', padding: '4px 10px', alignSelf: 'flex-start' }}
              onClick={() => change([...rows, { category: rows[rows.length - 1]?.category ?? null, item: '' }])}
            >
              ＋ 행 추가
            </button>
          </div>
        </div>
        <div className="sf-paper">
          <JournalPaper
            form={DEFAULT_FORMS.PATROL}
            journal={paper}
            siteName=""
            kindLabel=""
            photos={[]}
            patrolHeader={{
              ...header,
              signers: header.signers.filter((one) => one.trim()).length ? header.signers.filter((one) => one.trim()) : DEFAULT_PATROL_HEADER.signers,
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
